/**
 * Helper: General
 * -----------------------------------------------------------------------------
 * General helper utility functions.
 * - Functions which don't fit into any of the other utils files.
 * - Add in alphabetical order.
 *
 */

/**
 * Debounce functions.
 * - Calls a function when a user hasn't carried out an event for a specific
 *   amount of time.
 * @param {Function} callback The function to debounce.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function debounce(callback, wait, immediate) {
  let timeout = null

  return function(...args) {
    const callNow = immediate && !timeout

    function later() {
      timeout = null
      if (!immediate) {
        callback.apply(this, args)
      }
    }

    window.clearTimeout(timeout)
    timeout = window.setTimeout(later, wait)

    if (callNow) {
      callback.apply(this, args)
    }
  }
}

/**
 * Detects if a part of an element is in the viewport.
 * @param {HTMLElement} element - Element to check.
 * @returns {Boolean} - True if element is at least partially in viewport.
 */
export function isVisible(element) {
  const windowHeight = window.innerHeight

  const elementRectangle = element.getBoundingClientRect()
  const elementTop = elementRectangle.top
  const elementBottom = elementRectangle.bottom

  const topInView = (elementTop > 0) && (elementTop < windowHeight)
  const bottomInView = (elementBottom > 0) && (elementBottom < windowHeight)

  return (topInView || bottomInView)
}

/**
 * Scrolls page smoothly to element.
 * @param {HTMLElement} element - Element to scroll to.
 * @param {Object} data - Additional data.
 * @param {Function} data.callback - Function run after completion.
 * @param {HTMLElement} data.page - Element to scroll.
 */
export function scrollToElement(element, { callback, page = window } = {}) {
  let viewportFromPageTop = window.pageYOffset

  if (page !== window) {
    viewportFromPageTop = 0
  }

  const elementRectangle = element.getBoundingClientRect()
  const elementFromViewportTop = elementRectangle.top
  const position = (viewportFromPageTop + elementFromViewportTop)

  /**
   * Disable smooth scroll if prefers reduced motion.
   */
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth'

  page.scroll({
    behavior,
    left: 0,
    top: position,
  })

  if (typeof callback === 'function') {
    callback()
  }
}

/**
 * Set intersection observers to detect when add to cart button leaves viewport.
 * @param {Object} data - Data object.
 * @param {Object} data.callback - Function to run when intersecting.
 * @param {HTMLElement} data.element - Element to set observer on.
 * unobserved, must be a Vue element.
 * @param {String} [data.rootMargin] - This set of values serves to grow or shrink
 * each side of the root element's bounding box before computing intersections.
 * @param {Number|Array} [data.threshold] - Intersection observer threshold, 0.5
 * means the intersection is triggered when 50% of the element is in view.
 * @returns {Function}
 */
export function setIntersectionObserver({
  callback,
  element,
  rootMargin = '0px',
  threshold = 0.5,
}) {
  const options = {
    root: null,
    rootMargin,
    threshold,
  }

  const observer = new IntersectionObserver(callback, options)
  observer.observe(element)
}

/**
 * Throttle functions.
 * - Calls a function at intervals of a specified amount of time while the user
 *   is still carrying out the event.
 * @param {Function} callback - The function to execute when timer is passed.
 * @param {Number} wait - The amount of time before debounce call is triggered.
 * @param {Boolean} immediate - Trigger the immediately.
 */
export function throttle(callback, wait, immediate = false) {
  let timeout = null
  let initialCall = true

  return function(...args) {
    const callNow = immediate && initialCall

    function next() {
      callback.apply(this, args)
      timeout = null
    }

    if (callNow) {
      initialCall = false
      next()
    }

    if (!timeout) {
      timeout = window.setTimeout(next, wait)
    }
  }
}
